import { useEffect, useMemo, useState } from "react";
import { usePagination, useSortBy, useTable } from "react-table";
import moment from "moment";
import { X, Pencil, ChevronDown, ChevronRight } from "lucide-react";

import Loader from "../../../../reusableUI/loader";
import Page from "../../../../reusableUI/page";
import { capitalizeFirstLetter } from "../../../../utils/Functions";
import {
    getAssignedStaffList,
    actingHCPChange,
    timeSlotChange,

} from "../../../../Service/partnerAPI";
import { CommonImages } from "../../../../reusableUI/images";
import LiveTrackingMap from "../../../../reusableUI/LiveTrackingMap";
import PatientHistory from "../partnerPortal/patientHistory";
import { showToast } from "../../../../reusableUI/toaster";
import { IoMdBed } from "react-icons/io";
import { MdOutlineFilterList } from "react-icons/md";
import { FaUserNurse } from "react-icons/fa6";

/* ---------------- Small helpers ---------------- */

const toDashTiming = (label = "") => String(label || "").replace(" to ", " - ");

const SortIcon = ({ isSorted, isSortedDesc }) => {
    if (!isSorted) {
        return (
            <svg className="h-3 w-3 ml-1 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
                <path d="M5 12l5 5 5-5H5zm10-4L10 3 5 8h10z" />
            </svg>
        );
    }
    if (isSortedDesc) {
        return (
            <svg className="h-3 w-3 ml-1 text-blue-600" fill="currentColor" viewBox="0 0 20 20">
                <path d="M5 8l5 5 5-5H5z" />
            </svg>
        );
    }
    return (
        <svg className="h-3 w-3 ml-1 text-blue-600" fill="currentColor" viewBox="0 0 20 20">
            <path d="M15 12l-5-5-5 5h10z" />
        </svg>
    );
};

const normalizeId = (id) => {
    if (!id) return "";
    if (typeof id === "string") return id;
    if (typeof id === "object" && id.$oid) return String(id.$oid);
    return String(id);
};

const isUpcomingDate = (dateStr) => {
    if (!dateStr) return false;
    return moment(dateStr).startOf("day").isSameOrAfter(moment().startOf("day"));
};

const isTodayDate = (dateStr) => {
    if (!dateStr) return false;
    return moment(dateStr).isSame(moment(), "day");
};

const hasTodayDuty = (visit) => {
    const dutyInfo = Array.isArray(visit?.dutyInfo) ? visit.dutyInfo : [];
    return dutyInfo.some((duty) => isTodayDate(duty?.date));
};

const isTrackingVisibleForDuty = (duty) => {
    return (
        moment(duty?.date).format("YYYY-MM-DD") === moment().format("YYYY-MM-DD") &&
        String(duty?.liveStatus || "").toLowerCase() === "arriving"
    );
};

/* ---------------- ✅ REQUESTS FIX helpers ---------------- */

const isRequestsVisit = (visit) => {
    const st = String(visit?.status || "").trim().toLowerCase();
    return st === "pending" || st === "payment pending" || st === "not paid";
};

const formatServiceDateRange = (visitInfo) => {
    if (!visitInfo) return "-";
    const s = visitInfo?.startDate ? moment(visitInfo.startDate).format("DD/MM/YYYY") : "";
    const e = visitInfo?.endDate ? moment(visitInfo.endDate).format("DD/MM/YYYY") : "";
    if (s && e) return s === e ? s : `${s} - ${e}`;
    return s || e || "-";
};

const getDutyStaffNameFromVisit = (visit) =>
    visit?.dutyStaff?.name || visit?.specialistInfo?.name || visit?.assessmentStaff?.name || "N/A";

const getStandby1NameFromVisit = (visit) =>
    visit?.standBySpecialist?.name || visit?.standBySpecialists?.specialists1Name || "N/A";

const getStandby2NameFromVisit = (visit) =>
    visit?.backupSpecialist?.name || visit?.standBySpecialists?.specialists2Name || "N/A";

/**
 * Build per-day "virtual duties" when dutyInfo is empty (Requests).
 * Used for duty table sorting + reports modal so UI doesn't break.
 */
const buildVirtualDutiesFromVisitInfo = (visit) => {
    const startRaw = visit?.visitInfo?.startDate;
    const slot = String(visit?.visitInfo?.slot || "").trim();
    if (!startRaw || !slot) return [];

    const start = moment(startRaw).local().startOf("day");
    if (!start.isValid()) return [];

    const end = visit?.visitInfo?.endDate
        ? moment(visit.visitInfo.endDate).local().startOf("day")
        : null;

    const daysRaw = Number(visit?.visitInfo?.days);
    let totalDays = Number.isFinite(daysRaw) && daysRaw > 0 ? Math.floor(daysRaw) : 0;
    if (!totalDays && end?.isValid()) totalDays = end.diff(start, "days") + 1;
    if (!totalDays || totalDays <= 0) totalDays = 1;

    const staffName = getDutyStaffNameFromVisit(visit);
    const rows = [];
    for (let i = 0; i < totalDays; i++) {
        const d = start.clone().add(i, "day");
        if (end?.isValid() && d.isAfter(end, "day")) break;

        rows.push({
            _id: `virtual-${normalizeId(visit?._id) || "visit"}-${d.format("YYYY-MM-DD")}`,
            date: d.format("YYYY-MM-DD"),
            day: i + 1,
            slot,
            assignedStaffName: staffName,
            attendedBy: {
                role: visit?.specialistInfo?.role,
                userID: { $oid: normalizeId(visit?.dutyStaff?.userID || visit?.specialistInfo?.userID) },
            },
            startDuty: {},
            endDuty: {},
            liveStatus: "",
            payout: null,
            services: [],
        });
    }
    return rows;
};

const getDisplayDuties = (visit) => {
    if (!visit) return [];
    const dutyInfo = Array.isArray(visit?.dutyInfo) ? visit.dutyInfo : [];
    if (dutyInfo.length > 0) return dutyInfo;
    if (isRequestsVisit(visit)) return buildVirtualDutiesFromVisitInfo(visit);
    return [];
};

/* ---------------- Assessment / Treatment helpers ---------------- */

const getAssessments = (visit) => (Array.isArray(visit?.assessment) ? visit.assessment : []);

const getActiveAssessment = (visit) => {
    const arr = getAssessments(visit);
    return arr.find((a) => a?.active) || arr[0] || null;
};

// ✅ FIXED (your old expression returned "true"/"")
const getTreatmentPlanStatus = (visit) => {
    const assessment = getActiveAssessment(visit);
    return String(assessment?.treatmentPlan?.status || "").trim().toLowerCase();
};

const hasFurtherTreatmentRequested = (visit) => {
    const tpStatus = getTreatmentPlanStatus(visit);
    return tpStatus === "pending" || tpStatus === "requested";
};

const getFurtherTreatmentLabel = (visit) =>
    hasFurtherTreatmentRequested(visit) ? "Further Treatment Requested" : "";

/* ---------------- Visit status ---------------- */

const getVisitStatusLabel = (visit) => {
    const st = String(visit?.status || "").trim().toLowerCase();

    if (hasFurtherTreatmentRequested(visit) && visit.dutyInfo.length > 0) return "Ongoing";
    if (hasFurtherTreatmentRequested(visit) && visit.dutyInfo.length === 0 && visit.status !== "completed" && visit.status !== "expired" && visit.status !== "not paid") return "Payment pending";

    if (st === "accepted") return "Yet to Start";
    if (st === "startduty") return "Ongoing";
    if (st === "endduty") return "Ongoing";
    if (st === "completed") return "Completed";
    if (st === "rejected") return "Cancelled";
    if (st === "pending") return "Unassigned";
    if (st === "payment pending") return "Payment pending";
    if (st === "cancelled") return "Cancelled";
    if (st === "not paid") return "Not Paid";
    if (st === "not available") return "Not available";
    if (st === "expired") return "Expired";

    if (st.includes("startduty")) return "Ongoing";
    if (st.includes("endduty")) return "Completed";

    return st;
};

const StatusBadge = ({ status }) => {
    const map = {
        "Yet to Start": "text-orange-600",
        Ongoing: "text-blue-600",
        Completed: "text-teal-600",
        Cancelled: "text-red-700",
        Unassigned: "text-red-600",
        Requested: "text-orange-600",
        Unknown: "text-gray-500",
    };

    return (
        <span
            className={`inline-flex items-center justify-center px-2.5 py-1 text-[13px] font-medium font-poppins rounded-full ${map[status] || map.Unknown
                }`}
        >
            {status}
        </span>
    );
};

/* ---------------- Duty status ---------------- */

const isDutyStarted = (duty) => !!duty?.startDuty?.verified;
const isDutyEnded = (duty) => !!duty?.endDuty?.verified;

const getDutyStatusLabel = (visit, duty) => {
    const further = getFurtherTreatmentLabel(visit);

    if (!isDutyStarted(duty) && !isDutyEnded(duty)) return "Yet to Start";

    if (isDutyStarted(duty) && !isDutyEnded(duty)) {
        return further ? `Duty Started - ${further}` : "Duty Started";
    }

    if (isDutyEnded(duty)) {
        return further ? `End Duty - ${further}` : "End Duty";
    }

    return "Yet to Start";
};

const formatCheckTime = (t) => (t ? moment(t).format("hh:mm A") : "-");

/**
 * ✅ Robust assigned staff resolver (includes virtual assignedStaffName)
 */
const getDutyHcpName = (visit, duty) => {
    const nameInfo = duty?.attendedBy?.userID?.personalInfo?.basicInfo?.nameInfo;
    if (nameInfo?.fullName) {
        return `${nameInfo?.title ? nameInfo.title + " " : ""}${nameInfo.fullName}`.trim();
    }

    const dutyLevelName =
        duty?.specialistInfo?.name ||
        duty?.dutyStaff?.name ||
        duty?.actingHCP?.name ||
        duty?.actingHcpInfo?.name ||
        duty?.assignedStaff?.name ||
        duty?.assignedStaffName;

    if (dutyLevelName) return dutyLevelName;

    return visit?.dutyStaff?.name || visit?.specialistInfo?.name || "N/A";
};

/* ---------------- Duty time label ---------------- */

const formatDutyTimeFromStartEnd = (duty) => {
    const st = duty?.startTime?.$date || duty?.startTime;
    const et = duty?.endTime?.$date || duty?.endTime;
    if (!st || !et) return "";
    const startLocal = moment(st).local().format("hh:mm A");
    const endLocal = moment(et).local().format("hh:mm A");
    return `${startLocal} to ${endLocal}`;
};

const getDutyTimeLabel = (visit, duty, dutySlotsOverride, dutyKey) => {
    // if (dutySlotsOverride?.[dutyKey]) return dutySlotsOverride[dutyKey];
    // if (duty?.slot) return duty.slot;

    // const byStartEnd = formatDutyTimeFromStartEnd(duty);
    // if (byStartEnd) return byStartEnd;

    if (visit?.visitInfo?.slot) return visit.visitInfo.slot;

    // const tpLabel = getActiveAssessment(visit)?.treatmentPlan?.timings?.label;
    // if (tpLabel) return tpLabel;

    return "-";
};

/* ---------------- Payout helpers ---------------- */

const formatAmount = (n) => {
    if (n == null || n === "") return "";
    const num = Number(n);
    if (!Number.isFinite(num)) return "";
    return new Intl.NumberFormat("en-IN", { maximumFractionDigits: 2 }).format(num);
};

const getPayoutText = (duty) => {
    const amount = duty?.payout?.payoutToTheVendor?.amountToTransfer ?? duty?.payout?.fees ?? null;
    const status = duty?.payout?.payoutToTheVendor?.status ? String(duty.payout.payoutToTheVendor.status).toLowerCase() : "";
    const amtStr = formatAmount(amount);

    if (!amtStr && !status) return "-";
    if (amtStr && status) return `${"Rs. " + amtStr} (${status})`;
    if (amtStr) return "Rs. " + amtStr;
    return `(${status})`;
};

const getPayoutColorClass = (status) => {
    const st = String(status || "").toLowerCase();
    if (st === "processed" || st === "success" || st === "paid") return "text-green-700";
    if (st === "pending" || st === "initiated") return "text-orange-500";
    if (st === "failed" || st === "rejected") return "text-red-700";
    return "text-gray-700";
};

/* ---------------- Physio slots ---------------- */

const PHYSIO_TIME_SLOTS = [
    "06:00 AM to 07:00 AM",
    "07:00 AM to 08:00 AM",
    "08:00 AM to 09:00 AM",
    "09:00 AM to 10:00 AM",
    "10:00 AM to 11:00 AM",
    "11:00 AM to 12:00 PM",
    "12:00 PM to 01:00 PM",
    "01:00 PM to 02:00 PM",
    "02:00 PM to 03:00 PM",
    "03:00 PM to 04:00 PM",
    "04:00 PM to 05:00 PM",
    "05:00 PM to 06:00 PM",
    "06:00 PM to 07:00 PM",
    "07:00 PM to 08:00 PM",
    "08:00 PM to 09:00 PM",
];

const parseSelectedSlot = (slot) => {
    if (!slot) return { startTimeStr: "", endTimeStr: "" };
    const [from, to] = slot.split(" to ").map((s) => s.trim());
    return { startTimeStr: from || "", endTimeStr: to || "" };
};

const pickInitialSlotForModal = (currentSlot, visitSlot) => {
    if (PHYSIO_TIME_SLOTS.includes(currentSlot)) return currentSlot;
    if (PHYSIO_TIME_SLOTS.includes(visitSlot)) return visitSlot;
    return PHYSIO_TIME_SLOTS[0];
};

/* ---------------- Report UI helpers (unchanged) ---------------- */

const isEncryptedString = (v) => typeof v === "string" && v.startsWith("enc:v1:");
const truncate = (str, n = 42) => {
    const s = String(str ?? "");
    if (s.length <= n) return s;
    return s.slice(0, n) + "…";
};
const maybeDecrypt = (v) => v;

const formatValue = (val) => {
    if (val == null) return "-";
    if (typeof val === "boolean") return val ? "Yes" : "No";

    if (Array.isArray(val)) {
        if (!val.length) return "-";
        return val
            .map(maybeDecrypt)
            .map((x) => (isEncryptedString(x) ? truncate(x, 36) : String(x)))
            .join(", ");
    }

    if (typeof val === "object") {
        const entries = Object.entries(val || {});
        if (!entries.length) return "-";
        return entries.map(([k, v]) => `${k}: ${formatValue(v)}`).join("\n");
    }

    const v = maybeDecrypt(val);
    if (isEncryptedString(v)) return truncate(v, 36);

    const s = String(v).trim();
    return s ? s : "-";
};

const pickFromValues = (values, keys) => {
    const obj = values && typeof values === "object" ? values : {};
    for (const k of keys) {
        const v = obj?.[k];
        if (v != null && String(v).trim() !== "") return v;
    }
    return "";
};

const getServiceProvided = (values) =>
    pickFromValues(values, ["servicesProvided", "serviceProvided", "Services Provided", "Service Provided"]);
const getImprovements = (values) => pickFromValues(values, ["improvements", "Improvement", "Improvements"]);
const getServiceProvidedDetails = (values) =>
    pickFromValues(values, ["serviceProvidedDetails", "providedDetails", "Service Provided Details"]);
const getServiceNameField = (values) => pickFromValues(values, ["serviceName", "Service Name"]);
const getNote = (values, srv) =>
    pickFromValues(values, ["notes", "note", "Note", "Notes"]) || srv?.notes || srv?.note || "";

const PAIN_SCALE_LABELS = [
    { value: 0, label: "No Pain" },
    { value: 1, label: "Mild" },
    { value: 2, label: "Mild" },
    { value: 3, label: "Mild" },
    { value: 4, label: "Moderate" },
    { value: 5, label: "Moderate" },
    { value: 6, label: "Severe" },
    { value: 7, label: "Severe" },
    { value: 8, label: "Very Severe" },
    { value: 9, label: "Very Severe" },
    { value: 10, label: "Worst Pain Possible" },
];

const SERVICE_NAME_EXCLUDE_CHECKLIST = new Set([
    "back care",
    "vital signs",
    "nutrition",
    "suppository",
    "wound care",
    "continuous ryle's tube aspiration",
    "hourly ryle's tube aspiration",
    "additional service",
    "medication management",
]);

const asArray = (val) => (Array.isArray(val) ? val : []);
const asObject = (val) => (val && typeof val === "object" && !Array.isArray(val) ? val : {});
const hasText = (val) => String(val ?? "").trim().length > 0;

const getPainScaleText = (srv, rawValue) => {
    let n = NaN;
    if (srv?.value !== undefined && srv?.value !== null) {
        n = Number(srv.value);
    } else if (rawValue && typeof rawValue === "object") {
        n = Number(rawValue.painScale ?? rawValue.value);
    } else {
        n = Number(rawValue);
    }

    if (!Number.isFinite(n)) return "";
    const idx = Math.max(0, Math.min(10, Math.round(n)));
    const row = PAIN_SCALE_LABELS[idx];
    if (!row) return "";

    const severity = (rawValue && typeof rawValue === "object" && rawValue.severity) ? rawValue.severity : row.label;
    return `${row.value}/10 - ${severity}`;
};

/* ---------------- Filter helper ---------------- */

const getVisitBucket = (visit) => {
    const st = String(visit?.status || "").trim().toLowerCase();

    // ✅ Hard exits first — these must never fall into Live
    if (st === "expired") return "Others";
    if (st === "cancelled") return "Others";
    if (st === "not paid") return "Others";
    if (st === "not available") return "Others";
    if (st === "rejected") return "Completed";

    // ── Requests ──────────────────────────────────────────────────────────────
    if (st === "pending" || st === "payment pending") return "Payment Pending";

    // ── Live ──────────────────────────────────────────────────────────────────
    if (hasFurtherTreatmentRequested(visit)) return "Live";
    if (hasTodayDuty(visit)) return "Live";
    if (st === "startduty") return "Live";
    if (st.includes("startduty")) return "Live";

    // ── Completed ─────────────────────────────────────────────────────────────
    if (st === "completed") return "Completed";
    if (st === "endduty") return "Completed";
    if (st.includes("endduty")) return "Completed";

    // ── Upcoming ──────────────────────────────────────────────────────────────
    if (st === "accepted") return "Upcoming";

    return "Upcoming";
};

/* ---------------- Component ---------------- */

const Visits = ({ searchTerm, bookingsData, partnerID: partnerIDProp, from }) => {
    const isReadOnly = bookingsData !== undefined;

    const [loading, setLoading] = useState(true);
    const [visitList, setVisitList] = useState([]);
    const [paginationPage, setPaginationPage] = useState(0);
    const [selectedPageCount, setSelectedPageCount] = useState(() => {
        const saved = localStorage.getItem("visitsPageCount");
        return saved ? Number(saved) : 25;
    });
    const [selectedPatient, setSelectedPatient] = useState(null);

    const [dutySlots, setDutySlots] = useState({});
    const [editingDutyIndex, setEditingDutyIndex] = useState(null);
    const [changingDutyStaff, setChangingDutyStaff] = useState({});

    const [timeModalOpen, setTimeModalOpen] = useState(false);
    const [timeModalDutyIndex, setTimeModalDutyIndex] = useState(null);
    const [timeModalDutyKey, setTimeModalDutyKey] = useState(null);
    const [timeModalSelectedSlot, setTimeModalSelectedSlot] = useState("");

    const [reportsModalOpen, setReportsModalOpen] = useState(false);
    const [reportVisit, setReportVisit] = useState(null);
    const [expandedDays, setExpandedDays] = useState({});
    const [openingReportVisitId, setOpeningReportVisitId] = useState("");

    const [showTracking, setShowTracking] = useState(false);
    const [trackingData, setTrackingData] = useState(null);
    const [visitFilter, setVisitFilter] = useState(() => from);

    useEffect(() => {
        if (from) setVisitFilter(from);
        else setVisitFilter("All");
    }, [partnerIDProp, bookingsData, from]);

    const fetchVisits = async () => {
        if (bookingsData !== undefined) return Array.isArray(bookingsData) ? bookingsData : [];

        const result = await getAssignedStaffList();

        return Array.isArray(result?.data) ? result.data : [];
    };

    useEffect(() => {
        let cancelled = false;

        const load = async () => {
            setLoading(true);
            try {
                const list = await fetchVisits();
                if (!cancelled) setVisitList(list);
            } catch (error) {
                console.error("Error fetching assignment data:", error);
                if (!cancelled) setVisitList([]);
            } finally {
                if (!cancelled) setLoading(false);
            }
        };

        load();
        return () => {
            cancelled = true;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [partnerIDProp, bookingsData]);



    const filteredData = useMemo(() => {
        let filtered = Array.isArray(visitList) ? [...visitList] : [];
        filtered = visitFilter === "All" ? filtered : filtered.filter((v) => getVisitBucket(v) === visitFilter);

        if (searchTerm) {
            const term = searchTerm.toLowerCase();
            filtered = filtered.filter((item) => {
                const name =
                    item?.patient?.name?.toLowerCase() ||
                    item?.patientInfo?.personalInfo?.basicInfo?.nameInfo?.fullName?.toLowerCase() ||
                    "";
                const phone = String(item?.patient?.phone ?? item?.patientInfo?.phone ?? "");
                return name.includes(term) || phone.includes(term);
            });
        }

        return filtered;
    }, [visitList, searchTerm, visitFilter]);

    const selectedId = normalizeId(selectedPatient?._id);

    useEffect(() => {
        if (!filteredData.length) {
            if (selectedPatient !== null) setSelectedPatient(null);
            return;
        }

        if (!selectedId) {
            setSelectedPatient(filteredData[0]);
            return;
        }

        const updated = filteredData.find((x) => normalizeId(x?._id) === selectedId);
        if (!updated) {
            setSelectedPatient(filteredData[0]);
            return;
        }
        if (updated !== selectedPatient) setSelectedPatient(updated);
    }, [filteredData, selectedId]); // intentionally NOT depending on selectedPatient reference

    // ✅ FIX: duties for Requests come from visitInfo if dutyInfo empty
    const sortedDuties = useMemo(() => {
        const arr = getDisplayDuties(selectedPatient);
        return [...arr].sort((a, b) => moment(a?.date).valueOf() - moment(b?.date).valueOf());
    }, [selectedPatient]);

    const sortedReportDuties = useMemo(() => {
        const arr = getDisplayDuties(reportVisit);
        return [...arr].sort((a, b) => moment(a?.date).valueOf() - moment(b?.date).valueOf());
    }, [reportVisit]);

    const dischargeSummariesForHistory = useMemo(() => {
        const summaryDoc = selectedPatient?.visitInfo?.dischargeSummaryDoc;
        if (!summaryDoc) return [];
        if (Array.isArray(summaryDoc)) return summaryDoc.filter((item) => item && typeof item === "object");
        return typeof summaryDoc === "object" ? [summaryDoc] : [];
    }, [selectedPatient?.visitInfo?.dischargeSummaryDoc]);

    const handleRowClick = (patient) => {
        setSelectedPatient(patient);
        setDutySlots({});
        setEditingDutyIndex(null);
        setChangingDutyStaff({});
        setTimeModalOpen(false);
        setTimeModalDutyIndex(null);
        setTimeModalDutyKey(null);
        setTimeModalSelectedSlot("");
        setShowTracking(false);
        setTrackingData(null);
    };

    const calculateAge = (dob) => {
        if (!dob) return "-";
        const age = moment().diff(moment(dob), "years");
        return `${age} yrs`;
    };

    const isPhysiotherapist = selectedPatient?.specialistInfo?.role === "physiotherapist";

    const getStandbyStaffList = () => {
        if (!selectedPatient) return [];
        const standbyList = [];
        const sps = selectedPatient?.standBySpecialists;

        if (sps?.specialists1UserID)
            standbyList.push({ id: String(sps.specialists1UserID), name: sps.specialists1Name });
        if (sps?.specialists2UserID)
            standbyList.push({ id: String(sps.specialists2UserID), name: sps.specialists2Name });

        if (selectedPatient?.standBySpecialist?.userID) {
            standbyList.push({
                id: String(selectedPatient.standBySpecialist.userID),
                name: selectedPatient.standBySpecialist.name,
            });
        }

        if (selectedPatient?.backupSpecialist?.userID) {
            standbyList.push({
                id: String(selectedPatient.backupSpecialist.userID),
                name: selectedPatient.backupSpecialist.name,
            });
        }

        const seen = new Set();
        return standbyList.filter((s) => {
            if (!s?.id) return false;
            if (seen.has(s.id)) return false;
            seen.add(s.id);
            return true;
        });
    };

    const refetchVisits = async () => {
        if (bookingsData !== undefined) return;
        setLoading(true);
        try {
            const list = await fetchVisits();
            setVisitList(list);
        } catch (e) {
            console.error(e);
        } finally {
            setLoading(false);
        }
    };

    const handleDutyStaffChange = async (dutyIndex, staffId) => {
        if (isReadOnly) return;
        if (!selectedPatient || !staffId) return;

        const duty = sortedDuties?.[dutyIndex];
        if (!duty) return;

        const dutyStatus = getDutyStatusLabel(selectedPatient, duty);
        const canEdit = dutyStatus === "Yet to Start" && isUpcomingDate(duty?.date);

        if (!canEdit) {
            showToast("HCP change allowed only for upcoming duties (Yet to Start).", "Error");
            return;
        }

        const selectedStaff = getStandbyStaffList().find((s) => s.id === staffId);
        if (!selectedStaff) {
            showToast("Selected staff not found", "Error");
            return;
        }

        const fromName = getDutyHcpName(selectedPatient, duty);
        const confirmChange = window.confirm(
            `Are you sure you want to change staff for ${moment(duty.date).format(
                "DD/MM/YYYY"
            )}?\n\nFrom: ${fromName}\nTo: ${selectedStaff.name}`
        );
        if (!confirmChange) return;

        const payload = {
            visitID: selectedPatient._id,
            specialistID: selectedStaff.id,
            date: moment(duty.date).format("YYYY-MM-DD"),
        };

        try {
            await actingHCPChange(payload);
            showToast("Staff changed successfully!", "Success");

            setEditingDutyIndex(null);
            setChangingDutyStaff((prev) => {
                const copy = { ...prev };
                delete copy[dutyIndex];
                return copy;
            });

            await refetchVisits();
        } catch (err) {
            console.error(err);
            showToast("Failed to change staff. Please try again.", "Error");
        }
    };

    const handleCloseDropdown = (dutyIndex) => {
        setEditingDutyIndex(null);
        setChangingDutyStaff((prev) => {
            const copy = { ...prev };
            delete copy[dutyIndex];
            return copy;
        });
    };

    const openTimeModal = (dutyIndex, dutyKey, currentSlot) => {
        if (isReadOnly) return;
        if (!isPhysiotherapist) return;

        const duty = sortedDuties?.[dutyIndex];
        if (!duty) return;

        const dutyStatus = getDutyStatusLabel(selectedPatient, duty);
        const canEdit = dutyStatus === "Yet to Start" && isUpcomingDate(duty?.date);

        if (!canEdit) {
            showToast("Time slot change allowed only for upcoming duties (Yet to Start).", "Error");
            return;
        }

        const alertMsg =
            "Alert: Booking Slot Change Notice\n\n" +
            "You are attempting to change the scheduled booking slot for an assigned visit.\n" +
            "Please ensure that any changes are made only after clear communication and mutual agreement between you and the patient.\n\n" +
            'Kindly note that this process is solely between you and the patient. We ("Ceviliyar") do not participate in, monitor, or assume responsibility for any booking slot changes made.';

        const proceed = window.confirm(alertMsg);
        if (!proceed) return;

        setTimeModalDutyIndex(dutyIndex);
        setTimeModalDutyKey(dutyKey);
        setTimeModalSelectedSlot(pickInitialSlotForModal(currentSlot, selectedPatient?.visitInfo?.slot));
        setTimeModalOpen(true);
    };

    const closeTimeModal = () => {
        setTimeModalOpen(false);
        setTimeModalDutyIndex(null);
        setTimeModalDutyKey(null);
        setTimeModalSelectedSlot("");
    };

    const applySelectedTimeSlot = async () => {
        if (isReadOnly) return;
        if (!selectedPatient || timeModalDutyIndex == null || !timeModalSelectedSlot) return;

        const dutyForModal = sortedDuties?.[timeModalDutyIndex] || null;
        if (!dutyForModal) return;

        const dutyStatus = getDutyStatusLabel(selectedPatient, dutyForModal);
        const canEdit = dutyStatus === "Yet to Start" && isUpcomingDate(dutyForModal?.date);

        if (!canEdit) {
            showToast("Cannot change time after duty started / past date.", "Error");
            return;
        }

        const dateStr = moment(dutyForModal.date).format("YYYY-MM-DD");
        const { startTimeStr, endTimeStr } = parseSelectedSlot(timeModalSelectedSlot);

        if (!startTimeStr || !endTimeStr) {
            showToast("Invalid time slot selected", "Error");
            return;
        }

        const startMoment = moment(`${dateStr} ${startTimeStr}`, "YYYY-MM-DD hh:mm A");
        const endMoment = moment(`${dateStr} ${endTimeStr}`, "YYYY-MM-DD hh:mm A");

        const payload = {
            visitID: selectedPatient._id,
            startTime: startMoment.utc().format(),
            endTime: endMoment.utc().format(),
            date: dateStr,
        };

        try {
            const response = await timeSlotChange(payload);
            if (response?.error !== true) {
                showToast("Time slot updated successfully", "Success");
                if (timeModalDutyKey) {
                    setDutySlots((prev) => ({ ...prev, [timeModalDutyKey]: timeModalSelectedSlot }));
                }
                closeTimeModal();
                await refetchVisits();
            } else {
                showToast("Failed to update time slot", "Error");
            }
        } catch (err) {
            console.error("Error updating time slot:", err);
            showToast("Something went wrong while updating time slot", "Error");
        }
    };

    const dutyForModal = timeModalDutyIndex != null ? sortedDuties?.[timeModalDutyIndex] : null;

    const openLiveTracking = (visit, duty) => {
        const nameInfo = duty?.attendedBy?.userID?.personalInfo?.basicInfo?.nameInfo || {};
        const hcpName =
            `${nameInfo.title ? nameInfo.title + " " : ""}${nameInfo.fullName || ""}`.trim() ||
            visit?.specialistInfo?.name ||
            visit?.dutyStaff?.name ||
            "HCP";

        const hcpRole = duty?.attendedBy?.role || visit?.specialistInfo?.role || "HCP";
        const patientLocation = visit?.visitInfo?.addressInfo?.coordinates || null;

        setTrackingData({
            visitID: visit?._id,
            dutyID: duty?._id,
            patientLocation,
            hcpName,
            hcpRole,
        });
        setShowTracking(true);
    };

    const openAllReportsModal = async (visit) => {
        if (!visit) return;

        const visitId = normalizeId(visit?._id);
        setOpeningReportVisitId(visitId);

        let latestVisit = visit;
        try {
            if (bookingsData === undefined) {
                const list = await fetchVisits();
                if (Array.isArray(list)) {
                    setVisitList(list);
                    const freshMatch = list.find((row) => normalizeId(row?._id) === visitId);
                    if (freshMatch) latestVisit = freshMatch;
                }
            }
        } catch (error) {
            console.error("Failed to refresh latest report data before opening modal:", error);
            showToast("Unable to refresh latest report. Showing available data.", "Error");
        } finally {
            setOpeningReportVisitId("");
        }

        setSelectedPatient(latestVisit);
        setReportVisit(latestVisit);
        setReportsModalOpen(true);

        const duties = getDisplayDuties(latestVisit);
        duties.sort((a, b) => moment(a?.date).valueOf() - moment(b?.date).valueOf());

        const todayStr = moment().format("YYYY-MM-DD");
        const todayDuty = duties.find((d) => moment(d?.date).format("YYYY-MM-DD") === todayStr);

        const defaultDuty = todayDuty || duties[0];
        const defaultKey = normalizeId(defaultDuty?._id);

        setExpandedDays(defaultKey ? { [defaultKey]: true } : {});
    };

    const closeAllReportsModal = () => {
        setReportsModalOpen(false);
        setReportVisit(null);
        setExpandedDays({});
    };

    const toggleDay = (dutyKey) => {
        setExpandedDays((prev) => ({ ...prev, [dutyKey]: !prev?.[dutyKey] }));
    };

    const columns = useMemo(() => {
        return [
            {
                Header: () => <div className="font-poppins ml-4 whitespace-nowrap text-[13px]">Name</div>,
                accessor: "patient.name",
                Cell: ({ row: { original } }) => (
                    <div className="font-poppins flex items-center gap-2">
                        <div
                            className={`w-2 h-2 rounded-full ${normalizeId(selectedPatient?._id) === normalizeId(original?._id)
                                ? "bg-blue-600"
                                : "bg-transparent"
                                }`}
                        />
                        <p className="text-blue-600 truncate max-w-[140px] text-[13px]">
                            {original?.patient?.name || "N/A"}
                        </p>
                    </div>
                ),
            },
            {
                Header: () => <div className="font-poppins whitespace-nowrap text-[13px]">Gender</div>,
                accessor: "patient.gender",
                Cell: ({ row: { original } }) => (
                    <div className="font-poppins text-[13px]">
                        {original?.patient?.gender ? capitalizeFirstLetter(original.patient.gender) : "-"}
                    </div>
                ),
            },
            {
                Header: () => <div className="font-poppins whitespace-nowrap text-[13px]">Age</div>,
                accessor: "patient.dob",
                Cell: ({ row: { original } }) => (
                    <div className="font-poppins">
                        {calculateAge(original?.patientInfo?.personalInfo?.basicInfo?.otherInfo?.dob || null)}
                    </div>
                ),
            },
            {
                Header: () => <div className="font-poppins whitespace-nowrap text-[13px]">Service</div>,
                accessor: "specialistInfo.role",
                Cell: ({ row: { original } }) => (
                    <div className="text-[13px] font-poppins truncate max-w-[160px]">
                        {original?.specialistInfo?.role ? capitalizeFirstLetter(original.specialistInfo.role) : "-"}
                    </div>
                ),
            },
            {
                Header: () => <div className="font-poppins whitespace-nowrap text-[13px]">Location</div>,
                accessor: "visitInfo.addressInfo.city",
                Cell: ({ row: { original } }) => (
                    <div className="text-[12px] font-poppins truncate max-w-[160px] text-[13px]">
                        {original?.visitInfo?.addressInfo?.city
                            ? capitalizeFirstLetter(original.visitInfo.addressInfo.city)
                            : "-"}
                    </div>
                ),
            },
            {
                Header: () => <div className="font-poppins whitespace-nowrap text-[13px]">Duty Staff</div>,
                accessor: "specialistInfo.name",
                disableSortBy: true,
                Cell: ({ row: { original } }) => (
                    <div className="font-poppins text-blue-600">
                        <p className="truncate max-w-[140px] text-[13px]">
                            {original?.specialistInfo?.name || original?.dutyStaff?.name || "N/A"}
                        </p>
                    </div>
                ),
            },
            {
                Header: () => <div className="font-poppins whitespace-nowrap ml-2 text-[13px]">Status</div>,
                accessor: "status",
                Cell: ({ row: { original } }) => (
                    <div className="flex justify-start">
                        <StatusBadge status={getVisitStatusLabel(original)} />
                    </div>
                ),
            },
            {
                Header: () => <div className="font-poppins whitespace-nowrap text-[13px]">Report</div>,
                accessor: "report",
                disableSortBy: true,
                Cell: ({ row: { original } }) => {
                    const rowVisitId = normalizeId(original?._id);
                    const isOpening = openingReportVisitId === rowVisitId;
                    return (
                        <button
                            type="button"
                            disabled={isOpening}
                            onClick={(e) => {
                                e.stopPropagation();
                                openAllReportsModal(original);
                            }}
                            className={`text-[#4361EE] underline font-medium font-poppins text-[13px] ${isOpening ? "opacity-60 cursor-not-allowed" : "hover:opacity-80"
                                }`}
                        >
                            {isOpening ? "Loading..." : "View"}
                        </button>
                    );
                },
            },
        ];
    }, [selectedPatient, openingReportVisitId]);

    const {
        getTableProps,
        getTableBodyProps,
        headerGroups,
        page: tablePage,
        prepareRow,
        nextPage,
        previousPage,
        canPreviousPage,
        canNextPage,
        pageOptions,
        gotoPage,
        setPageSize,
        state: { pageIndex },
    } = useTable(
        {
            columns,
            data: filteredData,
            initialState: { pageSize: selectedPageCount, pageIndex: paginationPage },
            autoResetPage: false,
            autoResetSortBy: false,
        },
        useSortBy,
        usePagination
    );

    const handlePageCountChange = (val) => {
        const n = Number(val);
        if (!Number.isFinite(n)) return;
        localStorage.setItem("visitsPageCount", String(n));
        setSelectedPageCount(n);
        setPageSize(n);
        setPaginationPage(0);
        gotoPage(0);
    };

    useEffect(() => {
        const maxPage = Math.max(0, Math.ceil((filteredData?.length || 0) / selectedPageCount) - 1);
        if (paginationPage > maxPage) setPaginationPage(0);
    }, [filteredData?.length, paginationPage, selectedPageCount]);

    useEffect(() => {
        if (pageIndex !== paginationPage) setPaginationPage(pageIndex);
    }, [pageIndex, paginationPage]);

    if (loading) {
        return (
            <div>
                <Loader loading={loading} />
            </div>
        );
    }

    // ✅ Show Assign staff card when Requests context + request item + dutyInfo empty
    const isRequestsContext = String(from || "") === "Requests" || visitFilter === "Requests";
    const showAssignStaffCard =
        isRequestsContext &&
        selectedPatient &&
        isRequestsVisit(selectedPatient) &&
        (!Array.isArray(selectedPatient?.dutyInfo) || selectedPatient.dutyInfo.length === 0);

    const dutyStaffName = selectedPatient ? getDutyStaffNameFromVisit(selectedPatient) : "N/A";
    const standby1Name = selectedPatient ? getStandby1NameFromVisit(selectedPatient) : "N/A";
    const standby2Name = selectedPatient ? getStandby2NameFromVisit(selectedPatient) : "N/A";
    console.log(selectedPatient)
    return (
        <div className="flex flex-col min-h-screen gap-3 overflow-x-hidden">
            <div className="flex flex-row items-start justify-start w-full flex-1 min-h-0 gap-3 p-2">
                <div className="flex-1 flex flex-col gap-3 min-h-0">
                    <div className="bg-white rounded-2xl shadow-lg p-2 h-[44vh] flex flex-col justify-between">
                        <div className="flex justify-between items-center p-2 border-b border-gray-100 gap-3 flex-shrink-0">
                            <p className="text-[15px] font-semibold font-poppins whitespace-nowrap">
                                Patient List ({filteredData.length})
                            </p>

                            <div className="flex items-center gap-2">
                                <div className="flex items-center bg-white border border-blue-600 rounded-md px-2 h-[32px]" style={{ borderWidth: 1.5 }}>
                                    <select
                                        value={selectedPageCount}
                                        onChange={(e) => handlePageCountChange(e.target.value)}
                                        className="outline-none font-poppins text-[12px] text-blue-600 bg-transparent font-medium"
                                    >
                                        {[25, 50, 75, 100].map((n) => (
                                            <option key={n} value={n}>
                                                {n}
                                            </option>
                                        ))}
                                    </select>
                                </div>

                                <span className="text-[12px] text-gray-500 font-poppins whitespace-nowrap">
                                    <MdOutlineFilterList size={20} color={"blue"} />
                                </span>
                                <select
                                    value={visitFilter}
                                    onChange={(e) => {
                                        setVisitFilter(e.target.value);
                                        setPaginationPage(0);
                                    }}
                                    className="bg-[#F6F8FC] rounded-md px-2 py-1.5 text-[12px] font-poppins border border-gray-300 outline-none"
                                >
                                    <option value="All">All</option>
                                    <option value="Upcoming">Upcoming</option>
                                    <option value="Live">Live</option>
                                    <option value="Completed">Completed</option>
                                    <option value="Requests">Requests</option>
                                    <option value="Others">Others</option>
                                </select>
                            </div>
                        </div>

                        {filteredData.length === 0 ? (
                            <div className="flex flex-col items-center justify-center h-[35vh]">
                                <div className="w-14 h-14 rounded-full bg-[#EEF2FF] flex items-center justify-center mb-4">
                                    <img
                                        src={CommonImages.noDuty}
                                        className="h-[125px] w-[160px] object-contain"
                                        alt="No Duty"
                                    />
                                </div>
                                <h2 className="text-[13px] font-semibold font-poppins text-[#787998]">
                                    No visits available
                                </h2>
                                <p className="text-[11px] text-gray-500 font-poppins mt-1">Try changing the filter.</p>
                            </div>
                        ) : (
                            <div className="overflow-x-auto flex-1 min-h-0">
                                <div className="w-full h-full overflow-y-auto">
                                    <table {...getTableProps()} className="w-full">
                                        <thead className="bg-[#F6F8FC] sticky top-0 z-10">
                                            {headerGroups.map((headerGroup) => (
                                                <tr key={headerGroup.id} {...headerGroup.getHeaderGroupProps()}>
                                                    {headerGroup.headers.map((column) => (
                                                        <th
                                                            key={column.id}
                                                            {...column.getHeaderProps(column.getSortByToggleProps())}
                                                            className="px-3 py-2 text-left text-[12px] font-medium text-[#8177A8] tracking-wider cursor-pointer hover:bg-gray-100 whitespace-nowrap"
                                                        >
                                                            <div className="flex items-center gap-1">
                                                                {column.render("Header")}
                                                                {column.canSort && (
                                                                    <SortIcon isSorted={column.isSorted} isSortedDesc={column.isSortedDesc} />
                                                                )}
                                                            </div>
                                                        </th>
                                                    ))}
                                                </tr>
                                            ))}
                                        </thead>

                                        <tbody {...getTableBodyProps()} className="bg-white divide-y divide-gray-200">
                                            {tablePage.map((row) => {
                                                prepareRow(row);
                                                const isSelected =
                                                    normalizeId(selectedPatient?._id) === normalizeId(row.original?._id);

                                                return (
                                                    <tr
                                                        key={row.id}
                                                        {...row.getRowProps()}
                                                        onClick={() => handleRowClick(row.original)}
                                                        className={`cursor-pointer transition-colors ${isSelected ? "bg-[#DFEAFF] border-l-4 border-blue-600" : "hover:bg-gray-50"
                                                            }`}
                                                    >
                                                        {row.cells.map((cell) => (
                                                            <td
                                                                key={cell.column.id}
                                                                {...cell.getCellProps()}
                                                                className="px-3 py-1 text-[12px] text-gray-700 whitespace-nowrap"
                                                            >
                                                                {cell.render("Cell")}
                                                            </td>
                                                        ))}
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        )}

                        {filteredData.length > 0 && (
                            <div className="mt-2 mb-1 flex-shrink-0">
                                <Page
                                    paginationPage={paginationPage}
                                    nextPage={nextPage}
                                    previousPage={previousPage}
                                    canPreviousPage={canPreviousPage}
                                    canNextPage={canNextPage}
                                    pageOptions={pageOptions}
                                    screen="hospital"
                                />
                            </div>
                        )}
                    </div>

                    <div className="bg-white rounded-2xl shadow-lg p-3 h-[43vh] flex flex-col">
                        <h3 className="text-[14px] font-semibold font-poppins text-gray-800 mb-3 whitespace-nowrap">
                            {showAssignStaffCard ? "Assign staff" : "Duty Details"}
                        </h3>

                        {!selectedPatient ? (
                            <div className="flex-1 flex items-center justify-center text-[12px] text-gray-500 font-poppins">
                                No visit selected
                            </div>
                        ) : showAssignStaffCard ? (
                            /* ✅ Requests UI (your screenshot style) */
                            <div className="flex-1 overflow-y-auto">
                                <div className="border border-gray-200 rounded-xl overflow-hidden">
                                    <table className="min-w-full">
                                        <thead className="bg-[#F6F8FC] sticky top-0">
                                            <tr>
                                                <th className="px-4 py-2 text-left text-[12px] font-semibold text-[#8177A8] border-b font-poppins whitespace-nowrap">
                                                    Service Date
                                                </th>
                                                <th className="px-4 py-2 text-left text-[12px] font-semibold text-[#8177A8] border-b font-poppins whitespace-nowrap">
                                                    Time
                                                </th>
                                                <th className="px-4 py-2 text-left text-[12px] font-semibold text-[#8177A8] border-b font-poppins whitespace-nowrap">
                                                    Duty Staff
                                                </th>
                                            </tr>
                                        </thead>
                                        <tbody className="bg-white">
                                            <tr>
                                                <td className="px-4 py-2 text-[12px] font-poppins text-gray-700 whitespace-nowrap">
                                                    {formatServiceDateRange(selectedPatient?.visitInfo)}
                                                </td>
                                                <td className="px-4 py-2 text-[12px] font-poppins text-gray-700 whitespace-nowrap">
                                                    {selectedPatient?.visitInfo?.slot || "-"}
                                                </td>
                                                <td className="px-4 py-2 text-[12px] font-poppins text-gray-700 whitespace-nowrap">
                                                    <div className="flex items-center gap-2">
                                                        <img
                                                            src={CommonImages.nurseNew}
                                                            alt="Duty Staff"
                                                            className="w-6 h-6 rounded-full object-cover"
                                                        />
                                                        <span className="text-[#4361EE] font-medium truncate max-w-[220px]">
                                                            {dutyStaffName}
                                                        </span>
                                                    </div>
                                                </td>
                                            </tr>
                                        </tbody>
                                    </table>
                                </div>

                                <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-4">
                                    <div className="border border-gray-200 rounded-xl p-3">
                                        <p className="text-[12px] font-semibold font-poppins text-gray-800 mb-2 whitespace-nowrap">
                                            Standby Staff 1
                                        </p>
                                        <div className="flex items-center gap-2">
                                            <img
                                                src={CommonImages.nurseNew}
                                                alt="Standby 1"
                                                className="w-6 h-6 rounded-full object-cover"
                                            />
                                            <span className="text-[12px] font-poppins text-[#4361EE] font-medium truncate max-w-[220px]">
                                                {standby1Name}
                                            </span>
                                        </div>
                                    </div>

                                    <div className="border border-gray-200 rounded-xl p-3">
                                        <p className="text-[12px] font-semibold font-poppins text-gray-800 mb-2 whitespace-nowrap">
                                            Standby Staff 2
                                        </p>
                                        <div className="flex items-center gap-2">
                                            <img
                                                src={CommonImages.nurseNew}
                                                alt="Standby 2"
                                                className="w-6 h-6 rounded-full object-cover"
                                            />
                                            <span className="text-[12px] font-poppins text-[#4361EE] font-medium truncate max-w-[220px]">
                                                {standby2Name}
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        ) : (
                            /* ✅ Normal Duty table (your existing) */
                            <div className="flex-1 overflow-x-auto overflow-y-auto">
                                <table className="min-w-full border border-gray-200 rounded-[2px] overflow-hidden">
                                    <thead className="bg-[#F6F8FC] sticky top-0 z-10">
                                        <tr>
                                            <th className="px-3 py-2 text-left text-[13px] font-semibold text-[#8177A8] border-b font-poppins whitespace-nowrap">
                                                Date
                                            </th>
                                            <th className="px-3 py-2 text-left text-[13px] font-semibold text-[#8177A8] border-b font-poppins whitespace-nowrap">
                                                Time
                                            </th>
                                            <th className="px-3 py-2 text-left text-[13px] font-semibold text-[#8177A8] border-b font-poppins whitespace-nowrap">
                                                Assigned staff
                                            </th>
                                            <th className="px-3 py-2 text-center text-[12px] font-semibold text-[#8177A8] border-b font-poppins whitespace-nowrap">
                                                Check-in / Check-out
                                            </th>
                                            <th className="px-3 py-2 text-left text-[13px] font-semibold text-[#8177A8] border-b font-poppins whitespace-nowrap">
                                                Status
                                            </th>
                                            <th className="px-3 py-2 text-center text-[13px] font-semibold text-[#8177A8] border-b font-poppins whitespace-nowrap">
                                                Track
                                            </th>
                                            <th className="px-3 py-2 text-left text-[13px] font-semibold text-[#8177A8] border-b font-poppins whitespace-nowrap">
                                                Payout
                                            </th>
                                        </tr>
                                    </thead>

                                    <tbody className="bg-white divide-y divide-gray-200">
                                        {sortedDuties.length === 0 ? (
                                            <tr>
                                                <td colSpan={7} className="px-3 py-6 text-center text-[12px] text-gray-500 font-poppins">
                                                    No duty records available
                                                </td>
                                            </tr>
                                        ) : (
                                            sortedDuties.map((duty, index) => {
                                                const dutyKey = normalizeId(duty?._id) || String(index);
                                                const dutyStatus = getDutyStatusLabel(selectedPatient, duty);
                                                const canEditUpcoming = dutyStatus === "Yet to Start" && isUpcomingDate(duty?.date);
                                                const standbyList = getStandbyStaffList();

                                                const displayTime = getDutyTimeLabel(selectedPatient, duty, dutySlots, dutyKey);

                                                const started = !!duty?.startDuty?.verified;
                                                const payoutStatus = started ? duty?.payout?.payoutToTheVendor?.status || "" : "";
                                                const payoutText = started ? getPayoutText(duty) : "-";
                                                const canTrack = isTrackingVisibleForDuty(duty);

                                                return (
                                                    <tr key={dutyKey} className="hover:bg-[#DFEAFF]">
                                                        <td className="px-3 py-2 text-[13px] text-gray-700 font-poppins whitespace-nowrap">
                                                            {moment(duty.date).format("DD/MM/YYYY")}
                                                        </td>

                                                        <td className="px-3 py-2 text-[13px] text-gray-700 font-poppins whitespace-nowrap">
                                                            <div className="inline-flex items-center gap-2">
                                                                <span className="font-medium text-gray-800">{displayTime}</span>

                                                                {!isReadOnly && isPhysiotherapist && canEditUpcoming && (
                                                                    <button
                                                                        onClick={() => openTimeModal(index, dutyKey, displayTime)}
                                                                        className="text-[11px] text-blue-600 px-2 py-[2px] font-poppins"
                                                                        title="Edit time slot"
                                                                    >
                                                                        <img
                                                                            src={CommonImages.editIconBlue}
                                                                            className="text-[#4361EE] w-6 h-6"
                                                                            alt="Edit"
                                                                        />
                                                                    </button>
                                                                )}
                                                            </div>
                                                        </td>

                                                        <td className="px-3 py-2 text-[13px] font-poppins whitespace-nowrap">
                                                            {!isReadOnly && editingDutyIndex === index ? (
                                                                <div className="flex items-center gap-2">
                                                                    <select
                                                                        value={changingDutyStaff[index] || ""}
                                                                        onChange={(e) => {
                                                                            setChangingDutyStaff((prev) => ({
                                                                                ...prev,
                                                                                [index]: e.target.value,
                                                                            }));
                                                                            handleDutyStaffChange(index, e.target.value);
                                                                        }}
                                                                        autoFocus
                                                                        className="bg-[#F6F8FC] rounded-md px-2 py-1.5 text-[12px] font-poppins border border-gray-300"
                                                                    >
                                                                        <option value="" hidden>
                                                                            Select staff
                                                                        </option>
                                                                        {standbyList.map((s) => (
                                                                            <option key={s.id} value={s.id}>
                                                                                {s.name}
                                                                            </option>
                                                                        ))}
                                                                    </select>

                                                                    <button
                                                                        onClick={() => handleCloseDropdown(index)}
                                                                        className="p-1 rounded-full hover:bg-gray-200"
                                                                        title="Close"
                                                                    >
                                                                        <X size={14} className="text-gray-700" />
                                                                    </button>
                                                                </div>
                                                            ) : (
                                                                <div className="flex items-center gap-2">
                                                                    <span className="text-[#4361EE] font-medium">
                                                                        {getDutyHcpName(selectedPatient, duty)}
                                                                    </span>

                                                                    {!isReadOnly && canEditUpcoming && standbyList.length > 0 && (
                                                                        <button
                                                                            type="button"
                                                                            onClick={() => setEditingDutyIndex(index)}
                                                                            className="p-1 rounded-full hover:bg-blue-50"
                                                                            title="Change staff"
                                                                        >
                                                                            <Pencil size={14} className="text-[#4361EE]" />
                                                                        </button>
                                                                    )}
                                                                </div>
                                                            )}
                                                        </td>

                                                        <td className="px-3 text-center py-2 text-[13px] text-gray-700 font-poppins whitespace-nowrap">
                                                            {duty?.startDuty?.verified ? formatCheckTime(duty?.startDuty?.time) : "-"}
                                                            {" / "}
                                                            {duty?.endDuty?.verified ? formatCheckTime(duty?.endDuty?.time) : "-"}
                                                        </td>

                                                        <td className="px-3 py-2 text-[13px] font-poppins whitespace-nowrap">
                                                            <span className="text-gray-700">{dutyStatus}</span>
                                                        </td>

                                                        <td className="px-3 py-2 text-center text-[12px] font-poppins whitespace-nowrap">
                                                            {canTrack ? (
                                                                <button
                                                                    onClick={(e) => {
                                                                        e.stopPropagation();
                                                                        openLiveTracking(selectedPatient, duty);
                                                                    }}
                                                                    className="px-2 py-1 bg-green-100 text-green-700 text-[10px] rounded-md hover:bg-green-200 transition-colors inline-flex items-center gap-1 font-poppins whitespace-nowrap"
                                                                >
                                                                    <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                        <path
                                                                            strokeLinecap="round"
                                                                            strokeLinejoin="round"
                                                                            strokeWidth={2}
                                                                            d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 111.314 0z"
                                                                        />
                                                                        <path
                                                                            strokeLinecap="round"
                                                                            strokeLinejoin="round"
                                                                            strokeWidth={2}
                                                                            d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
                                                                        />
                                                                    </svg>
                                                                    Track
                                                                </button>
                                                            ) : (
                                                                <span className="text-gray-400">-</span>
                                                            )}
                                                        </td>

                                                        <td className="px-3 py-2 text-[13px] font-poppins whitespace-nowrap">
                                                            <span className={`text-[12px] font-medium ${getPayoutColorClass(payoutStatus)}`}>
                                                                {payoutText}
                                                            </span>
                                                        </td>
                                                    </tr>
                                                );
                                            })
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>
                </div>

                <PatientHistory selectedPatient={selectedPatient} dischargeSummaries={dischargeSummariesForHistory} />
            </div>

            {timeModalOpen && !isReadOnly && isPhysiotherapist && selectedPatient && (
                <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
                    <div className="bg-white rounded-2xl shadow-xl w-full max-w-md mx-4 p-4">
                        <div className="flex items-center justify-between mb-3">
                            <h3 className="text-[15px] font-semibold font-poppins text-gray-800">Select Time Slot</h3>
                            <button onClick={closeTimeModal} className="p-1 rounded-full hover:bg-gray-100">
                                <X size={18} className="text-gray-700" />
                            </button>
                        </div>

                        {dutyForModal && (
                            <p className="text-[11px] text-gray-500 mb-3 font-poppins">
                                {moment(dutyForModal.date).format("DD/MM/YYYY - dddd")}
                            </p>
                        )}

                        <div className="max-h-[260px] overflow-y-auto mb-4">
                            <div className="grid grid-cols-2 gap-2">
                                {PHYSIO_TIME_SLOTS.map((slot) => {
                                    const active = timeModalSelectedSlot === slot;
                                    return (
                                        <button
                                            key={slot}
                                            type="button"
                                            onClick={() => setTimeModalSelectedSlot(slot)}
                                            className={`px-3 py-2 rounded-lg text-[12px] font-poppins border text-center transition-colors ${active
                                                ? "bg-[#4951EC] border-[#4951EC] text-white"
                                                : "bg-white border-gray-300 text-gray-800 hover:bg-gray-50"
                                                }`}
                                        >
                                            {slot}
                                        </button>
                                    );
                                })}
                            </div>
                        </div>

                        <div className="flex justify-end gap-2">
                            <button
                                type="button"
                                onClick={closeTimeModal}
                                className="px-3 py-1.5 rounded-md text-[12px] font-poppins bg-gray-100 text-gray-700 hover:bg-gray-200"
                            >
                                Cancel
                            </button>
                            <button
                                type="button"
                                onClick={applySelectedTimeSlot}
                                disabled={!timeModalSelectedSlot}
                                className="px-3 py-1.5 rounded-md text-[12px] font-poppins bg-[#4951EC] text-white hover:bg-[#3d3acc] disabled:bg-gray-300 disabled:cursor-not-allowed"
                            >
                                Apply
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* REPORTS MODAL (your original code) */}
            {reportsModalOpen && reportVisit && (
                <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-3">
                    <div className="bg-white w-[95vw] max-w-4xl rounded-2xl shadow-2xl border overflow-hidden">
                        <div className="px-6 py-4 flex items-center justify-between border-b bg-white">
                            <div className="min-w-0">
                                <h3 className="text-[14px] font-semibold font-poppins text-gray-900">Reports</h3>
                                <p className="text-[10px] text-gray-500 font-poppins mt-0.5 truncate">
                                    {reportVisit?.jobID ? `Job ID: ${reportVisit.jobID} • ` : ""}
                                    {reportVisit?.patient?.name || "Patient"}
                                </p>
                            </div>

                            <button
                                onClick={closeAllReportsModal}
                                className="h-9 w-9 inline-flex items-center justify-center rounded-full hover:bg-gray-100 transition"
                                title="Close"
                            >
                                <X size={18} className="text-gray-700" />
                            </button>
                        </div>

                        <div className="px-6 py-5 max-h-[75vh] overflow-y-auto">
                            <div className="rounded-2xl border border-blue-100 overflow-hidden bg-white">
                                <div className="grid grid-cols-1 sm:grid-cols-2">
                                    <div className="px-4 py-3 border-b sm:border-b-0 sm:border-r border-blue-50">
                                        <div className="flex items-center gap-2 text-[10px] text-gray-500 font-poppins">
                                            <IoMdBed className="text-[#3E57E8]" size={18} />
                                            Patient
                                        </div>
                                        <div className="text-[12px] text-gray-900 font-semibold font-poppins mt-1 truncate">
                                            {reportVisit?.patient?.name || "Patient"}
                                        </div>
                                    </div>

                                    <div className="px-4 py-3">
                                        <div className="flex items-center gap-2 text-[10px] text-gray-500 font-poppins">
                                            <FaUserNurse className="text-[#3E57E8]" size={14} />
                                            Staff
                                        </div>
                                        <div className="text-[12px] text-gray-900 font-semibold font-poppins mt-1 truncate">
                                            {reportVisit?.specialistInfo?.name || reportVisit?.dutyStaff?.name || "N/A"}
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className="mt-5 space-y-4">
                                {sortedReportDuties.length === 0 ? (
                                    <div className="rounded-xl border border-dashed p-6 text-center text-[11px] text-gray-500 font-poppins">
                                        No duty records available.
                                    </div>
                                ) : (
                                    sortedReportDuties.map((duty, idx) => {
                                        const dutyKey = normalizeId(duty?._id) || String(idx);
                                        const isOpen = !!expandedDays?.[dutyKey];

                                        const timingLabel = getDutyTimeLabel(reportVisit, duty, dutySlots, dutyKey);

                                        const services = Array.isArray(duty?.services) ? duty.services : [];
                                        const sortedServices = [...services].sort((a, b) => {
                                            const av = a?.dateTime ? moment(a.dateTime).valueOf() : 0;
                                            const bv = b?.dateTime ? moment(b.dateTime).valueOf() : 0;
                                            return av - bv;
                                        });

                                        return (
                                            <div key={dutyKey} className="rounded-2xl border-2 border-[#3E57E8] overflow-hidden bg-white">
                                                <div className="grid grid-cols-1 sm:grid-cols-12">
                                                    <div className="sm:col-span-4 border-b sm:border-b-0 sm:border-r border-blue-200 p-4 text-center">
                                                        <div className="text-[13px] font-semibold font-poppins text-gray-900">
                                                            {moment(duty.date).format("DD MMM YYYY")}
                                                        </div>
                                                        <div className="text-[11px] font-poppins text-gray-500 mt-1">
                                                            Day {duty?.day ?? idx + 1}
                                                        </div>
                                                    </div>

                                                    <div className="sm:col-span-8 p-4">
                                                        <button
                                                            type="button"
                                                            onClick={() => toggleDay(dutyKey)}
                                                            className="w-full flex items-center justify-between gap-3"
                                                        >
                                                            <div className="min-w-0 text-left">
                                                                <div className="text-[14px] font-semibold font-poppins text-[#3E57E8]">Reports</div>
                                                                <div className="text-[11px] font-poppins text-gray-600 mt-1 truncate">
                                                                    Timing: {toDashTiming(timingLabel)}
                                                                </div>
                                                            </div>

                                                            <div className="shrink-0 flex items-center gap-2 text-[#3E57E8]">
                                                                <span className="text-[11px] font-poppins">{isOpen ? "Hide" : "Show"}</span>
                                                                {isOpen ? <ChevronDown size={18} /> : <ChevronRight size={18} />}
                                                            </div>
                                                        </button>
                                                    </div>
                                                </div>

                                                {isOpen && (
                                                    <div className="border-t border-blue-200 p-4">
                                                        {sortedServices.length === 0 ? (
                                                            <div className="rounded-xl border border-dashed p-6 text-center text-[11px] text-gray-500 font-poppins">
                                                                No report available for this day.
                                                            </div>
                                                        ) : (
                                                            <div className="relative">
                                                                <div className="absolute left-[18px] top-2 bottom-2 w-[3px] bg-emerald-500/70 rounded-full" />

                                                                <div className="space-y-10">
                                                                    {sortedServices.map((srv, sidx) => {
                                                                        const id = normalizeId(srv?._id) || String(sidx);
                                                                        const timeOnly = srv?.dateTime ? moment(srv.dateTime).format("hh:mm A") : "-";
                                                                        const rawValues = srv?.values;
                                                                        const values = asObject(rawValues);

                                                                        const title = srv?.name || srv?.serviceName || `Service ${sidx + 1}`;
                                                                        const serviceNameKey = String(title || "").trim().toLowerCase();
                                                                        const role = String(reportVisit?.specialistInfo?.role || "").trim().toLowerCase();
                                                                        const isPhysiotherapist = role === "physiotherapist";
                                                                        const checklistValues = asArray(rawValues).map((v) => String(v ?? "").trim()).filter(Boolean);

                                                                        const servicesProvided = getServiceProvided(values);
                                                                        const improvements = getImprovements(values);
                                                                        const serviceProvidedDetails = getServiceProvidedDetails(values);
                                                                        const explicitServiceName = getServiceNameField(values);
                                                                        const note = getNote(values, srv);
                                                                        const painScaleText = getPainScaleText(srv, rawValues);
                                                                        const additionalServiceName = pickFromValues(values, ["name", "serviceName", "service", "Service Name"]);
                                                                        const additionalProvidedDetail = pickFromValues(values, [
                                                                            "providedDetail",
                                                                            "providedDetails",
                                                                            "serviceProvidedDetails",
                                                                            "Service Provided Details",
                                                                        ]);

                                                                        const medicationDoseRows = (() => {
                                                                            if (Array.isArray(values?.doseProvidedTime)) {
                                                                                return values.doseProvidedTime
                                                                                    .map((row) => ({
                                                                                        value: String(row?.value ?? "").trim(),
                                                                                        time: row?.dateTime ? moment(row.dateTime).format("h:mm A") : "",
                                                                                    }))
                                                                                    .filter((row) => hasText(row.value));
                                                                            }

                                                                            const doseMap = asObject(values?.doseProvidedTime);
                                                                            return Object.keys(doseMap)
                                                                                .filter((doseKey) => Boolean(doseMap[doseKey]))
                                                                                .map((doseKey) => ({
                                                                                    value: doseKey,
                                                                                    time: srv?.dateTime ? moment(srv.dateTime).format("h:mm A") : "",
                                                                                }));
                                                                        })();

                                                                        const isPainScale = serviceNameKey === "pain scale";
                                                                        const isAdditionalService = serviceNameKey === "additional service";
                                                                        const isVitalSigns = serviceNameKey === "vital signs";
                                                                        const isBackCare = serviceNameKey === "back care";
                                                                        const isNutrition = serviceNameKey === "nutrition";
                                                                        const isSuppository = serviceNameKey === "suppository";
                                                                        const isWoundCare = serviceNameKey === "wound care";
                                                                        const isContinuousRyles = serviceNameKey === "continuous ryle's tube aspiration";
                                                                        const isHourlyRyles = serviceNameKey === "hourly ryle's tube aspiration";
                                                                        const isMedicationManagement = serviceNameKey === "medication management";
                                                                        const nutritionCheckedText =
                                                                            checklistValues.length > 0
                                                                                ? checklistValues.map((item) => `checked ${item}`).join("\n")
                                                                                : (typeof rawValues === "string" || typeof rawValues === "number")
                                                                                    ? `checked ${String(rawValues).trim()}`
                                                                                    : "";

                                                                        const hasAny =
                                                                            (isPainScale &&
                                                                                hasText(painScaleText)) ||
                                                                            (isPhysiotherapist &&
                                                                                !isAdditionalService &&
                                                                                !isPainScale &&
                                                                                (hasText(servicesProvided) || hasText(improvements))) ||
                                                                            (!isPhysiotherapist &&
                                                                                checklistValues.length > 0 &&
                                                                                !SERVICE_NAME_EXCLUDE_CHECKLIST.has(serviceNameKey)) ||
                                                                            (!isPhysiotherapist &&
                                                                                isVitalSigns &&
                                                                                (hasText(values?.temperature) ||
                                                                                    hasText(values?.Spo2) ||
                                                                                    hasText(values?.bloodPressure) ||
                                                                                    hasText(values?.bloodGlucose) ||
                                                                                    hasText(values?.pulseRate) ||
                                                                                    hasText(values?.respiratoryRate))) ||
                                                                            (!isPhysiotherapist &&
                                                                                isBackCare &&
                                                                                (hasText(values?.site) ||
                                                                                    hasText(values?.grade) ||
                                                                                    hasText(values?.action))) ||
                                                                            (!isPhysiotherapist &&
                                                                                isNutrition &&
                                                                                hasText(nutritionCheckedText)) ||
                                                                            (!isPhysiotherapist &&
                                                                                isSuppository &&
                                                                                (hasText(values?.type) ||
                                                                                    hasText(values?.medication) ||
                                                                                    hasText(values?.dosage) ||
                                                                                    hasText(note))) ||
                                                                            (!isPhysiotherapist &&
                                                                                isWoundCare &&
                                                                                (hasText(values?.location) ||
                                                                                    hasText(values?.type) ||
                                                                                    hasText(values?.length) ||
                                                                                    hasText(values?.width) ||
                                                                                    hasText(values?.depth) ||
                                                                                    hasText(values?.drainageAmount) ||
                                                                                    hasText(values?.drainageType) ||
                                                                                    hasText(note))) ||
                                                                            (!isPhysiotherapist &&
                                                                                (isContinuousRyles || isHourlyRyles) &&
                                                                                (hasText(values?.rate) ||
                                                                                    hasText(values?.tube) ||
                                                                                    hasText(values?.formula) ||
                                                                                    hasText(values?.feedingType) ||
                                                                                    hasText(note))) ||
                                                                            (isAdditionalService &&
                                                                                (hasText(additionalServiceName) ||
                                                                                    hasText(additionalProvidedDetail) ||
                                                                                    hasText(note))) ||
                                                                            (!isPhysiotherapist &&
                                                                                isMedicationManagement &&
                                                                                (hasText(values?.type) ||
                                                                                    hasText(values?.name) ||
                                                                                    hasText(values?.dose) ||
                                                                                    hasText(values?.doseTime) ||
                                                                                    hasText(values?.mealTime) ||
                                                                                    medicationDoseRows.length > 0)) ||
                                                                            (!isPhysiotherapist &&
                                                                                !isPainScale &&
                                                                                !isVitalSigns &&
                                                                                !isBackCare &&
                                                                                !isNutrition &&
                                                                                !isSuppository &&
                                                                                !isWoundCare &&
                                                                                !isContinuousRyles &&
                                                                                !isHourlyRyles &&
                                                                                !isAdditionalService &&
                                                                                !isMedicationManagement &&
                                                                                (hasText(servicesProvided) ||
                                                                                    hasText(improvements) ||
                                                                                    hasText(serviceProvidedDetails) ||
                                                                                    hasText(note) ||
                                                                                    hasText(explicitServiceName)));

                                                                        const renderBlock = (label, val) => (
                                                                            <div>
                                                                                <div className="text-[13px] font-semibold font-poppins text-gray-900">
                                                                                    {label !== "Pain Scale" ? label : ""}
                                                                                </div>
                                                                                <div className="text-[12px] font-poppins text-gray-600 mt-1 whitespace-pre-line break-words">
                                                                                    {formatValue(val)}
                                                                                </div>
                                                                            </div>
                                                                        );

                                                                        return (
                                                                            <div key={id} className="relative pl-12">
                                                                                <div className="absolute left-[10px] top-[6px]">
                                                                                    <div className="h-5 w-5 rounded-full bg-white border-[4px] border-emerald-500" />
                                                                                </div>

                                                                                <div className="text-[13px] font-semibold font-poppins text-[#3E57E8]">
                                                                                    {timeOnly}
                                                                                </div>

                                                                                <div
                                                                                    className="mt-2 text-[15px] font-semibold font-poppins text-[#3E57E8] break-words"
                                                                                    title={String(title || "")}
                                                                                >
                                                                                    {formatValue(title)}
                                                                                </div>

                                                                                <div className="mt-4 space-y-5">
                                                                                    {!hasAny ? (
                                                                                        <div className="text-[11px] text-gray-500 font-poppins">
                                                                                            No values recorded.
                                                                                        </div>
                                                                                    ) : (
                                                                                        <>
                                                                                            {isPainScale ? (
                                                                                                renderBlock("Pain Scale", painScaleText)
                                                                                            ) : null}

                                                                                            {isPhysiotherapist && !isAdditionalService && !isPainScale ? (
                                                                                                <>
                                                                                                    {hasText(servicesProvided)
                                                                                                        ? renderBlock("Services Provided", servicesProvided)
                                                                                                        : null}
                                                                                                    {hasText(improvements)
                                                                                                        ? renderBlock("Improvements", improvements)
                                                                                                        : null}
                                                                                                </>
                                                                                            ) : null}

                                                                                            {!isPhysiotherapist &&
                                                                                                checklistValues.length > 0 &&
                                                                                                !SERVICE_NAME_EXCLUDE_CHECKLIST.has(serviceNameKey) ? (
                                                                                                <div className="space-y-1">
                                                                                                    {checklistValues.map((item, idx2) => (
                                                                                                        <div
                                                                                                            key={`${id}-checked-${idx2}`}
                                                                                                            className="text-[12px] font-poppins text-gray-600 mt-1 whitespace-pre-line break-words"
                                                                                                        >
                                                                                                            checked {item}
                                                                                                        </div>
                                                                                                    ))}
                                                                                                </div>
                                                                                            ) : null}

                                                                                            {!isPhysiotherapist && isVitalSigns ? (
                                                                                                <>
                                                                                                    {hasText(values?.temperature)
                                                                                                        ? renderBlock("Temperature (°F)", values.temperature)
                                                                                                        : null}
                                                                                                    {hasText(values?.Spo2) ? renderBlock("SpO2", values.Spo2) : null}
                                                                                                    {hasText(values?.bloodPressure)
                                                                                                        ? renderBlock("Blood Pressure (mmHg)", values.bloodPressure)
                                                                                                        : null}
                                                                                                    {hasText(values?.bloodGlucose)
                                                                                                        ? renderBlock("Blood Glucose (mg/dL)", values.bloodGlucose)
                                                                                                        : null}
                                                                                                    {hasText(values?.pulseRate)
                                                                                                        ? renderBlock("Pulse (bpm)", values.pulseRate)
                                                                                                        : null}
                                                                                                    {hasText(values?.respiratoryRate)
                                                                                                        ? renderBlock("Respiratory Rate", values.respiratoryRate)
                                                                                                        : null}
                                                                                                </>
                                                                                            ) : null}

                                                                                            {!isPhysiotherapist && isBackCare ? (
                                                                                                <>
                                                                                                    {hasText(values?.site) ? renderBlock("Site", values.site) : null}
                                                                                                    {hasText(values?.grade) ? renderBlock("Grade", values.grade) : null}
                                                                                                    {hasText(values?.action)
                                                                                                        ? renderBlock("Action Taken", values.action)
                                                                                                        : null}
                                                                                                </>
                                                                                            ) : null}

                                                                                            {!isPhysiotherapist && isNutrition ? (
                                                                                                renderBlock("Nutrition", nutritionCheckedText)
                                                                                            ) : null}

                                                                                            {!isPhysiotherapist && isSuppository ? (
                                                                                                <>
                                                                                                    {hasText(values?.type)
                                                                                                        ? renderBlock("Type of Suppository", values.type)
                                                                                                        : null}
                                                                                                    {hasText(values?.medication)
                                                                                                        ? renderBlock("Medication Name", values.medication)
                                                                                                        : null}
                                                                                                    {hasText(values?.dosage)
                                                                                                        ? renderBlock("Dosage (mg/IU)", values.dosage)
                                                                                                        : null}
                                                                                                    {hasText(note) ? renderBlock("Note", note) : null}
                                                                                                </>
                                                                                            ) : null}

                                                                                            {!isPhysiotherapist && isWoundCare ? (
                                                                                                <>
                                                                                                    {hasText(values?.location)
                                                                                                        ? renderBlock("Wound Location", values.location)
                                                                                                        : null}
                                                                                                    {hasText(values?.type)
                                                                                                        ? renderBlock("Wound Type", values.type)
                                                                                                        : null}
                                                                                                    {(hasText(values?.length) || hasText(values?.width) || hasText(values?.depth))
                                                                                                        ? renderBlock(
                                                                                                            "Wound Size",
                                                                                                            `Length: ${values?.length || "-"} cm\nWidth: ${values?.width || "-"} cm\nDepth: ${values?.depth || "-"} cm`,
                                                                                                        )
                                                                                                        : null}
                                                                                                    {hasText(values?.drainageAmount)
                                                                                                        ? renderBlock("Drainage Amount", values.drainageAmount)
                                                                                                        : null}
                                                                                                    {hasText(values?.drainageType)
                                                                                                        ? renderBlock("Drainage Type", values.drainageType)
                                                                                                        : null}
                                                                                                    {hasText(note) ? renderBlock("Note", note) : null}
                                                                                                </>
                                                                                            ) : null}

                                                                                            {!isPhysiotherapist && (isContinuousRyles || isHourlyRyles) ? (
                                                                                                <>
                                                                                                    {hasText(values?.rate) ? renderBlock("Rate (mL/Hr)", values.rate) : null}
                                                                                                    {hasText(values?.tube) ? renderBlock("Tube", values.tube) : null}
                                                                                                    {hasText(values?.formula)
                                                                                                        ? renderBlock("Formula", values.formula)
                                                                                                        : null}
                                                                                                    {hasText(values?.feedingType)
                                                                                                        ? renderBlock("Feeding Type", values.feedingType)
                                                                                                        : null}
                                                                                                    {hasText(note) ? renderBlock("Note", note) : null}
                                                                                                </>
                                                                                            ) : null}

                                                                                            {isAdditionalService ? (
                                                                                                <>
                                                                                                    {hasText(additionalServiceName)
                                                                                                        ? renderBlock("Service Name", additionalServiceName)
                                                                                                        : null}
                                                                                                    {hasText(additionalProvidedDetail)
                                                                                                        ? renderBlock("Service Provided Details", additionalProvidedDetail)
                                                                                                        : null}
                                                                                                    {hasText(note) ? renderBlock("Note", note) : null}
                                                                                                </>
                                                                                            ) : null}

                                                                                            {!isPhysiotherapist && isMedicationManagement ? (
                                                                                                <>
                                                                                                    {hasText(values?.type) ? renderBlock("Drug Type", values.type) : null}
                                                                                                    {hasText(values?.name) ? renderBlock("Drug Name", values.name) : null}
                                                                                                    {hasText(values?.dose) ? renderBlock("Dose", values.dose) : null}
                                                                                                    {hasText(values?.doseTime)
                                                                                                        ? renderBlock("Dose Timing", values.doseTime)
                                                                                                        : null}
                                                                                                    {hasText(values?.mealTime)
                                                                                                        ? renderBlock("Meal Time", values.mealTime)
                                                                                                        : null}
                                                                                                    {medicationDoseRows.length > 0
                                                                                                        ? renderBlock(
                                                                                                            "Time Provided",
                                                                                                            medicationDoseRows
                                                                                                                .map((row) =>
                                                                                                                    row.time
                                                                                                                        ? `${row.value} - ${row.time}`
                                                                                                                        : row.value,
                                                                                                                )
                                                                                                                .join("\n"),
                                                                                                        )
                                                                                                        : null}
                                                                                                </>
                                                                                            ) : null}

                                                                                            {!isPhysiotherapist &&
                                                                                                !isPainScale &&
                                                                                                !isVitalSigns &&
                                                                                                !isBackCare &&
                                                                                                !isNutrition &&
                                                                                                !isSuppository &&
                                                                                                !isWoundCare &&
                                                                                                !isContinuousRyles &&
                                                                                                !isHourlyRyles &&
                                                                                                !isAdditionalService &&
                                                                                                !isMedicationManagement &&
                                                                                                !SERVICE_NAME_EXCLUDE_CHECKLIST.has(serviceNameKey) &&
                                                                                                checklistValues.length === 0 ? (
                                                                                                <>
                                                                                                    {hasText(explicitServiceName)
                                                                                                        ? renderBlock("Service Name", explicitServiceName)
                                                                                                        : null}
                                                                                                    {hasText(servicesProvided)
                                                                                                        ? renderBlock("Services Provided", servicesProvided)
                                                                                                        : null}
                                                                                                    {hasText(improvements)
                                                                                                        ? renderBlock("Improvements", improvements)
                                                                                                        : null}
                                                                                                    {hasText(serviceProvidedDetails)
                                                                                                        ? renderBlock("Service Provided Details", serviceProvidedDetails)
                                                                                                        : null}
                                                                                                    {hasText(note) ? renderBlock("Note", note) : null}
                                                                                                </>
                                                                                            ) : null}
                                                                                        </>
                                                                                    )}
                                                                                </div>
                                                                            </div>
                                                                        );
                                                                    })}
                                                                </div>
                                                            </div>
                                                        )}
                                                    </div>
                                                )}
                                            </div>
                                        );
                                    })
                                )}
                            </div>
                        </div>

                        <div className="px-6 py-4 border-t bg-white flex justify-end">
                            <button
                                onClick={closeAllReportsModal}
                                className="px-5 py-2 rounded-xl bg-[#3E57E8] text-white text-[12px] font-semibold font-poppins hover:bg-[#3249d6] transition"
                            >
                                Close
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {showTracking && trackingData && (
                <LiveTrackingMap
                    visible={showTracking}
                    onClose={() => setShowTracking(false)}
                    visitID={trackingData.visitID}
                    dutyID={trackingData.dutyID}
                    patientLocation={trackingData.patientLocation}
                    hcpName={trackingData.hcpName}
                    hcpRole={trackingData.hcpRole}
                />
            )}
        </div>
    );
};

export default Visits;